// Reports view — Target / Consumed / Adherence table shown above the "Daily
// macro breakdown" chart (the legacy Classic view's AdherenceStats table).
// Deliberately uses the SAME markup and class names as AdherenceStats.js
// (adherence-row-stats / adherence-row-titles / adherence-clean-body, one
// <section role="table-wrapper"><table role="report-table"> per macro) so it
// inherits Classic's global styling — paddings, font sizes, rounded corners —
// instead of a second copy of it to keep in sync. One column per macro
// (Protein/Carbs/Fat with Grams + % of kcal, Fiber Grams only, kCal), rows for
// Target / Consumed / Adherence and an Under/Over verdict. Numbers come from
// buildMacroAdherenceTable (analysis.js) — the same logged-with-target days the
// chart plots — so this table and the Summary/Calendar/Log views share one
// engine.

import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";

import { MACRO_COLORS, translateMacroLabel } from "./viewTokens";

// Column order. `shares` = also shows a "% of kcal" column (only protein/
// carbs/fat). `colored` = title gets its macro color; Fiber and kCal titles
// stay the default (white) text color, like Classic (its `var(--fiber)` /
// `var(--kCal)` don't exist, so those titles fall back to the inherited color).
const COLUMNS = [
  { key: "proteins", shares: true, colored: true },
  { key: "carbs", shares: true, colored: true },
  { key: "fats", shares: true, colored: true },
  { key: "fibers", shares: false, colored: false },
  { key: "kcal", shares: false, colored: false },
];

// Left-hand row labels, top to bottom; null = blank row (header rows).
const ROW_TITLES = [null, null, "target", "consumed", "adherence"];

const at = (t, key) => t(`nutritionPage.adherenceStats.${key}`);

// 0 / missing renders as "-" (Classic's evaluateValue), never a bare "0g".
const fmtAmount = (value, key) => {
  if (!value) return "-";
  return key === "kcal" ? `${Math.abs(value)}` : `${Math.abs(value)}g`;
};
const fmtPercent = (value) => (value ? `${Math.abs(value)}%` : "-");

const verdict = (t, adherence) => {
  if (adherence > 0) return at(t, "under");
  if (adherence < 0) return at(t, "over");
  return "-";
};

const MacroColumn = ({ column, data, t }) => {
  const { key, shares, colored } = column;
  const row = (value, percent) => (
    <tr>
      <td>{fmtAmount(value, key)}</td>
      {shares && <td>{fmtPercent(percent)}</td>}
    </tr>
  );

  return (
    // The custom roles below aren't ARIA — they're the hooks Classic's CSS
    // (reports/nutrition.css) keys its table styling on, kept so this table
    // renders identically.
    // biome-ignore lint/a11y/useValidAriaRole: styling hook shared with Classic's AdherenceStats
    <section role="table-wrapper">
      {/* biome-ignore lint/a11y/useValidAriaRole: styling hook shared with Classic's AdherenceStats */}
      <table role="report-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: styling hook shared with Classic's AdherenceStats */}
        <thead role="table-header">
          <tr>
            <td colSpan={2}>
              <span style={colored ? { color: MACRO_COLORS[key] } : undefined}>
                {translateMacroLabel(t, key)}
              </span>
            </td>
          </tr>
        </thead>
        {/* biome-ignore lint/a11y/useValidAriaRole: styling hook shared with Classic's AdherenceStats */}
        <tbody role="table-body">
          <tr>
            {key === "kcal" ? (
              <th className="border-none!" />
            ) : (
              <>
                <th className="border-none!">{at(t, "grams")}</th>
                {shares && <th className="border-none!">%</th>}
              </>
            )}
          </tr>
          {row(data.target, data.targetShare)}
          {row(data.consumed, data.consumedShare)}
          {row(data.adherence, data.adherencePct)}
          <tr>
            <td colSpan={2}>
              <span>{verdict(t, data.adherence)}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  );
};

MacroColumn.propTypes = {
  column: PropTypes.shape({
    key: PropTypes.string.isRequired,
    shares: PropTypes.bool,
    colored: PropTypes.bool,
  }).isRequired,
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export const ReportsMacroTable = ({ table }) => {
  const { t } = useTranslation("athlete-portal");
  if (!table) return null;

  return (
    // Centered like Classic's `.adherence-table`; auto side-margins on the child
    // (not justify-center) so an overflowing table still scrolls from its left edge.
    <div className="overflow-x-auto my-4">
      <div className="adherence-row-stats w-fit mx-auto">
        <div className="adherence-row-titles">
          {/* 2 blank rows line up with the title + Grams/% header rows of each
              column. <label> because Classic's `.adherence-row-titles > label`
              sizing rule targets that tag. */}
          {ROW_TITLES.map((title, i) => (
            // biome-ignore lint/a11y/noLabelWithoutControl: layout label, not a form control (same as Classic)
            <label key={title ?? `blank-${i}`}>{title && at(t, title)}</label>
          ))}
        </div>
        <div className="adherence-clean-body">
          {COLUMNS.map((column) => (
            <MacroColumn
              key={column.key}
              column={column}
              data={table[column.key]}
              t={t}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

ReportsMacroTable.propTypes = {
  table: PropTypes.object,
};

export default ReportsMacroTable;
